import { ImagePickerAsset } from "expo-image-picker";
import { useRouter } from "expo-router";
import { FileText } from "lucide-react-native";
import { FC, useState } from "react";
import { ScrollView } from "react-native";

import {
  Box,
  Button,
  ButtonText,
  Card,
  HStack,
  Input,
  InputField,
  MediaPicker,
  Pressable,
  Text,
  Textarea,
  TextareaInput,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { Study, StudyType } from "@/types/resources";
import { UseCase } from "@/types/utils";

interface CreateStudyProps {
  className?: string;
  folderId?: string | null;
  initialDescription?: string;
  initialName?: string;
  onCancel?: () => void;
  onCreated?: (study: Study) => void;
  studyId?: string;
  /**
   * When set, the study is created for a specific quick-insights use case:
   * the type is forced to `quantitative`, the type picker and the
   * quick-insights nudge are hidden, and `use_case` is sent on create.
   */
  useCase?: UseCase;
}

const CreateStudy: FC<CreateStudyProps> = ({
  className,
  folderId,
  initialDescription = "",
  initialName = "",
  onCancel,
  onCreated,
  studyId,
  useCase,
}) => {
  const { i18n } = useLocalization();
  const router = useRouter();
  const [description, setDescription] = useState(initialDescription);
  const [images, setImages] = useState<ImagePickerAsset[]>([]);
  const [name, setName] = useState(initialName);
  const [studyType, setStudyType] = useState<StudyType>(
    useCase ? "quantitative" : "qualitative"
  );

  const {
    createStudy,
    error: createError,
    loading: createLoading,
    uploadProgress,
  } = API.Studies.useCreateStudy();

  const handleSubmit = async () => {
    const study = await createStudy({
      description: description || null,
      folder_id: folderId ?? null,
      name,
      picture: images[0],
      study_id: studyId,
      type: studyType,
      use_case: useCase ?? null,
    });

    if (study && onCreated) {
      onCreated(study);
    }
  };

  const isDuplicating = !!studyId;

  const isFormValid = name.trim().length > 0;

  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      <Card
        className={`border border-outline-200 rounded-2xl p-6 ${className || ""}`}
      >
        {/* Header + study type */}
        <VStack className="gap-6 mb-6">
          <Box className="flex-row items-center gap-3">
            <Box className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-300/20 to-secondary-300/20 items-center justify-center">
              <FileText color="rgb(var(--color-primary-500))" size={24} />
            </Box>
            <VStack className="flex-1">
              <Text className="text-2xl font-bold text-typography-900">
                {isDuplicating
                  ? i18n.t("studies.duplicateStudy")
                  : !onCancel
                    ? i18n.t("studies.createFirstStudy")
                    : i18n.t("studies.createNewStudy")}
              </Text>
              <Text className="text-sm text-typography-600">
                {isDuplicating
                  ? i18n.t("studies.duplicateStudyDescription")
                  : !onCancel
                    ? i18n.t("studies.createFirstStudyDescription")
                    : i18n.t("studies.createNewStudyDescription")}
              </Text>
            </VStack>
          </Box>

          {!studyId && !useCase && (
            <VStack className="gap-3">
              <Text className="text-sm font-semibold text-typography-800">
                {i18n.t("studies.studyType")}
              </Text>
              <HStack className="gap-4 flex-col sm:flex-row">
                <Pressable
                  className={`w-full sm:flex-1 p-5 rounded-xl border-2 ${
                    studyType === "qualitative"
                      ? "border-primary-500 bg-primary-500/10"
                      : "border-outline-200 bg-background-50"
                  }`}
                  onPress={() => setStudyType("qualitative")}
                >
                  <Text
                    className={`text-base font-bold mb-2 ${
                      studyType === "qualitative"
                        ? "text-primary-500"
                        : "text-typography-900"
                    }`}
                  >
                    {i18n.t("studies.qualitativeShort")}
                  </Text>
                  <Text className="text-sm text-typography-600 leading-5">
                    {i18n.t("studies.qualitativeDescription")}
                  </Text>
                </Pressable>
                <Pressable
                  className={`w-full sm:flex-1 p-5 rounded-xl border-2 ${
                    studyType === "quantitative"
                      ? "border-primary-500 bg-primary-500/10"
                      : "border-outline-200 bg-background-50"
                  }`}
                  onPress={() => setStudyType("quantitative")}
                >
                  <Text
                    className={`text-base font-bold mb-2 ${
                      studyType === "quantitative"
                        ? "text-primary-500"
                        : "text-typography-900"
                    }`}
                  >
                    {i18n.t("studies.quantitativeShort")}
                  </Text>
                  <Text className="text-sm text-typography-600 leading-5">
                    {i18n.t("studies.quantitativeDescription")}
                  </Text>
                </Pressable>
              </HStack>
            </VStack>
          )}

          <VStack className="gap-2">
            <Text className="text-sm font-semibold text-typography-800">
              {i18n.t("studies.coverPicture")}
            </Text>
            <MediaPicker
              compact
              height={128}
              maxItems={1}
              media={images}
              mediaType="images"
              onMediaChange={(media) => setImages(media as ImagePickerAsset[])}
              width="100%"
            />
          </VStack>
        </VStack>

        <VStack className="gap-6">
          <VStack>
            <Text className="text-sm font-semibold text-typography-800 mb-2">
              {i18n.t("studies.studyName")}
            </Text>
            <Input className="border border-outline-200 rounded-xl bg-background-50">
              <InputField
                maxLength={50}
                onChangeText={setName}
                placeholder={i18n.t("studies.studyNamePlaceholder")}
                value={name}
              />
            </Input>
          </VStack>

          <VStack className="gap-2">
            <Text className="text-sm font-semibold text-typography-800">
              {i18n.t("studies.studyDescription")}
            </Text>
            <Textarea className="border border-outline-200 rounded-xl bg-background-50">
              <TextareaInput
                onChangeText={setDescription}
                placeholder={i18n.t("studies.studyDescriptionPlaceholder")}
                value={description}
              />
            </Textarea>
          </VStack>

          <VStack className="gap-2">
            <HStack className="gap-3 justify-end">
              {onCancel && (
                <Button
                  className="px-6 py-3 rounded-lg"
                  isDisabled={createLoading}
                  onPress={onCancel}
                  variant="outline"
                >
                  <ButtonText>{i18n.t("misc.cancel")}</ButtonText>
                </Button>
              )}
              <Button
                className="px-6 py-3 rounded-lg bg-primary-500"
                isDisabled={createLoading || !isFormValid}
                onPress={handleSubmit}
              >
                <ButtonText className="text-white">
                  {uploadProgress > 0
                    ? i18n.t("misc.uploadingMedia", {
                        count: images.length,
                        percent: i18n.numberToPercentage(uploadProgress * 100, {
                          precision: 0,
                        }),
                      })
                    : createLoading
                      ? isDuplicating
                        ? i18n.t("misc.duplicating")
                        : i18n.t("misc.creatingStudy")
                      : isDuplicating
                        ? i18n.t("misc.duplicate")
                        : i18n.t("misc.create")}
                </ButtonText>
              </Button>
            </HStack>
            {createError && (
              <Text className="text-error-600 text-sm text-right">
                {createError}
              </Text>
            )}
          </VStack>
        </VStack>

        {!isDuplicating && !useCase && (
          <Text className="text-sm text-typography-400 text-right mt-4">
            {i18n.t("studies.quickInsightsNudge")}{" "}
            <Text
              className="text-sm text-primary-500 font-medium"
              onPress={() => router.push("/quick-insights")}
            >
              {i18n.t("dashboard.quickInsights")}
            </Text>
          </Text>
        )}
      </Card>
    </ScrollView>
  );
};

export default CreateStudy;
